import { useEffect, useRef } from 'react'
import echarts from './config'

/**
 * Manages the ECharts instance lifecycle: init, dispose, resize,
 * option updates, loading state, and event binding.
 *
 * Returns a ref to the chart instance for imperative access.
 */
export function useECharts (containerRef, options, {
  autoResize = true,
  loading = false,
  loadingConfig,
  onChartReady,
  onEvents
} = {}) {
  const chartRef = useRef(null)
  const callbackRefs = useRef({ onChartReady })
  callbackRefs.current.onChartReady = onChartReady

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const chart = echarts.init(el, undefined, {
      renderer: 'canvas',
      useDirtyRect: true
    })
    chartRef.current = chart
    callbackRefs.current.onChartReady?.(chart)

    const handleResize = () => chart.resize()
    window.addEventListener('resize', handleResize)
    const observer = autoResize ? new ResizeObserver(handleResize) : null
    if (observer) observer.observe(el)

    return () => {
      window.removeEventListener('resize', handleResize)
      observer?.disconnect()
      chart.dispose()
      chartRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const chart = chartRef.current
    if (!chart || !options) return
    try {
      chart.setOption(options, true)
    } catch (err) {
      console.error('ECharts setOption failed:', err)
    }
  }, [options])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    if (loading) {
      chart.showLoading('default', loadingConfig || {
        text: 'Loading...',
        textColor: '#000',
        maskColor: 'rgba(255, 255, 255, 0.8)'
      })
    } else {
      chart.hideLoading()
    }
  }, [loading, loadingConfig])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart || !onEvents) return
    const entries = Object.entries(onEvents)
    entries.forEach(([name, handler]) => chart.on(name, handler))
    return () => entries.forEach(([name, handler]) => chart.off(name, handler))
  }, [onEvents])

  return chartRef
}
